import React, { useState, useEffect, useCallback } from 'react';
import {
  FaTwitch,
  FaSpotify,
  FaPlug,
  FaSpinner,
  FaCrown,
  FaRobot,
  FaChevronDown,
} from 'react-icons/fa';
import { ROUTES, APP_VERSION, STORAGE_KEYS } from '../config/constants';
import { storage } from '../utils/storage';
import { getPremiumFeatureIdForDashboardTab } from '../config/dashboardPremiumFeatures';
import PremiumBadge from './premium/PremiumBadge';
import PremiumTrialButton from './premium/PremiumTrialButton';
import UserAccountCard from './dashboard/UserAccountCard';
import '../styles/premium.css';

// Composant d'icône SVG réutilisable
const Icon = ({ children, size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const RewardsIcon = () => (
  <Icon>
    <path d="M12 2L2 7l10 5 10-5-10-5z" />
    <path d="M2 17l10 5 10-5" />
    <path d="M2 12l10 5 10-5" />
  </Icon>
);

const SpotifyIcon = () => (
  <Icon>
    <circle cx="12" cy="12" r="10" />
    <path d="M8 12l3 3 5-6" />
  </Icon>
);

const SettingsIcon = () => (
  <Icon>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 1v6m0 6v6M5.64 5.64l4.24 4.24m4.24 4.24l4.24 4.24M1 12h6m6 0h6M5.64 18.36l4.24-4.24m4.24-4.24l4.24-4.24" />
  </Icon>
);

const CommandsIcon = () => (
  <Icon>
    <polyline points="4 17 10 11 4 5" />
    <line x1="12" y1="19" x2="20" y2="19" />
  </Icon>
);

const FollowIcon = () => (
  <Icon>
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="8.5" cy="7" r="4" />
    <path d="M20 8v6M23 11l-3 3-3-3" />
  </Icon>
);

const MentionsIcon = () => (
  <Icon>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="10" r="3" />
    <path d="M6 10h12" />
  </Icon>
);

const HomeIcon = () => (
  <Icon>
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </Icon>
);

const FollowsSubsIcon = () => (
  <Icon>
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    <path d="M12 8v4m0 4h.01" />
  </Icon>
);

const StatsIcon = () => (
  <Icon>
    <path d="M4 19h16" />
    <rect x="5" y="11" width="3" height="7" />
    <rect x="10.5" y="7" width="3" height="11" />
    <rect x="16" y="4" width="3" height="14" />
  </Icon>
);

const OverviewIcon = () => (
  <Icon>
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
  </Icon>
);

const BirthdayIcon = () => (
  <Icon>
    <path d="M20 21H4a1 1 0 0 1-1-1v-7h18v7a1 1 0 0 1-1 1z" />
    <path d="M3 13h18" />
    <path d="M6 13V9a3 3 0 0 1 3-3h6a3 3 0 0 1 3 3v4" />
    <path d="M12 6V3" />
    <path d="M12 3c1 0 2 .9 2 2 0 1.6-2 2.4-2 4-0-1.6-2-2.4-2-4 0-1.1 1-2 2-2z" />
  </Icon>
);

const ClipsIcon = () => (
  <Icon>
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M10 8l6 4-6 4V8z" />
  </Icon>
);

const OverlaysIcon = () => (
  <Icon>
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <circle cx="8" cy="8" r="2" />
    <circle cx="16" cy="8" r="2" />
    <circle cx="8" cy="16" r="2" />
    <circle cx="16" cy="16" r="2" />
  </Icon>
);

const SIDEBAR_SECTION_IDS = [
  'principal',
  'integrations',
  'stream',
  'automation',
  'settings',
];

const TAB_TO_SIDEBAR_SECTION = {
  overview: 'principal',
  rewards: 'integrations',
  spotify: 'integrations',
  followed: 'integrations',
  mentions: 'integrations',
  followssubs: 'integrations',
  streamstats: 'stream',
  birthdays: 'stream',
  clips: 'stream',
  overlays: 'stream',
  commands: 'automation',
  ai: 'automation',
  parameters: 'settings',
};

function readSidebarSectionState() {
  const raw = storage.get(STORAGE_KEYS.SIDEBAR_NAV_SECTIONS);
  const next = {};
  for (const id of SIDEBAR_SECTION_IDS) {
    next[id] = raw && typeof raw[id] === 'boolean' ? raw[id] : true;
  }
  return next;
}

/**
 * Groupe de navigation repliable (desktop) ; aplati en liste horizontale sur mobile.
 * @param {Object} props
 * @param {string} props.sectionId
 * @param {string} props.title
 * @param {boolean} props.isOpen
 * @param {() => void} props.onToggle
 * @param {React.ReactNode} props.children - éléments li contenant les navButton
 */
function NavSection({ sectionId, title, isOpen, onToggle, children }) {
  const panelId = `nav-section-panel-${sectionId}`;
  const headingId = `nav-section-heading-${sectionId}`;

  return (
    <li className={`navSection ${isOpen ? 'navSection--open' : ''}`}>
      <button
        type="button"
        className="navSectionToggle"
        aria-expanded={isOpen}
        aria-controls={panelId}
        id={headingId}
        onClick={onToggle}
      >
        <span className="navSectionToggleLabel">{title}</span>
        <FaChevronDown className="navSectionToggleChevron" aria-hidden />
      </button>
      <div
        className="navSectionPanel"
        id={panelId}
        role="region"
        aria-labelledby={headingId}
      >
        <ul className="navSectionItems">{children}</ul>
      </div>
    </li>
  );
}

function Sidebar({
  activeTab,
  switchTab,
  navigate,
  hasNewMentions = false,
  hasNewFollowedChannelOnline = false,
  onConnectAll = null,
  isTwitchConnected = false,
  isSpotifyConnected = false,
  isPremium = false,
  isTrial = false,
  eligibleForTrial = false,
  onRequestTrial = null,
  twitchUser = null,
  user = null,
  premiumFeatureGating = null,
}) {
  const [isConnecting, setIsConnecting] = useState(false);
  const [sectionOpen, setSectionOpen] = useState(readSidebarSectionState);

  const toggleSection = useCallback((sectionId) => {
    setSectionOpen((prev) => {
      const next = { ...prev, [sectionId]: !prev[sectionId] };
      storage.set(STORAGE_KEYS.SIDEBAR_NAV_SECTIONS, next);
      return next;
    });
  }, []);

  useEffect(() => {
    const sid = TAB_TO_SIDEBAR_SECTION[activeTab];
    if (!sid) return;
    setSectionOpen((prev) => {
      if (prev[sid]) return prev;
      const next = { ...prev, [sid]: true };
      storage.set(STORAGE_KEYS.SIDEBAR_NAV_SECTIONS, next);
      return next;
    });
  }, [activeTab]);

  const showPremiumCrownForTab = (tabKey) => {
    const id = getPremiumFeatureIdForDashboardTab(tabKey);
    if (!id) return false;
    if (
      premiumFeatureGating &&
      typeof premiumFeatureGating === 'object' &&
      premiumFeatureGating[id] === false
    ) {
      return false;
    }
    return true;
  };

  const handleGoHome = () => {
    if (navigate) {
      navigate(ROUTES.HOME);
    }
  };

  const handleConnectAll = async () => {
    if (!onConnectAll) return;
    setIsConnecting(true);
    try {
      await onConnectAll();
    } finally {
      setIsConnecting(false);
    }
  };

  // Déterminer l'état de connexion
  const bothConnected = isTwitchConnected && isSpotifyConnected;

  return (
    <aside className="sidebar">
      <div className="sidebarHeader">
        <h3 className="sidebarTitle">Dashboard</h3>
        <button
          className="homeButton"
          onClick={handleGoHome}
          title="Retour à l'accueil"
        >
          <HomeIcon />
          <span>Accueil</span>
        </button>

        {/* Carte compte utilisateur connecté */}
        <UserAccountCard
          twitchUser={twitchUser}
          user={user}
          isPremium={isPremium}
          isTrial={isTrial}
        />

        {/* Bouton de connexion rapide Twitch + Spotify */}
        {onConnectAll && !bothConnected && (
          <button
            className="connectAllButton"
            onClick={handleConnectAll}
            disabled={isConnecting}
            title="Connecter Twitch et Spotify"
          >
            {isConnecting ? (
              <>
                <FaSpinner className="spinner" />
                <span>Connexion...</span>
              </>
            ) : (
              <>
                <FaPlug />
                <span>Connecter les apps</span>
                <div className="connectAllIcons">
                  <FaTwitch className={isTwitchConnected ? 'connected' : ''} />
                  <FaSpotify
                    className={isSpotifyConnected ? 'connected' : ''}
                  />
                </div>
              </>
            )}
          </button>
        )}

        {/* Badge Premium / Essai et bouton Activer l'essai gratuit */}
        <PremiumBadge
          isPremium={isPremium}
          isTrial={isTrial}
          className="sidebarPremiumBadge"
        />
        {onRequestTrial && (
          <PremiumTrialButton
            eligibleForTrial={eligibleForTrial}
            onRequestTrial={onRequestTrial}
            className="sidebar-trial"
          />
        )}
      </div>

      <nav
        className="sidebarNav"
        aria-label="Navigation du tableau de bord"
      >
        <ul className="navList">
          <NavSection
            sectionId="principal"
            title="Principal"
            isOpen={sectionOpen.principal}
            onToggle={() => toggleSection('principal')}
          >
            <li>
              <button
                className={`navButton ${
                  activeTab === 'overview' ? 'active' : ''
                }`}
                onClick={() => switchTab('overview')}
              >
                <OverviewIcon />
                <span>Vue d'ensemble</span>
              </button>
            </li>
          </NavSection>

          <NavSection
            sectionId="integrations"
            title="Intégrations"
            isOpen={sectionOpen.integrations}
            onToggle={() => toggleSection('integrations')}
          >
            <li>
              <button
                className={`navButton ${showPremiumCrownForTab('rewards') ? 'premium-feature' : ''} ${activeTab === 'rewards' ? 'active' : ''}`}
                onClick={() => switchTab('rewards')}
                title={
                  showPremiumCrownForTab('rewards')
                    ? 'Récompenses (Premium)'
                    : undefined
                }
              >
                <RewardsIcon />
                <span>Récompenses</span>
                {showPremiumCrownForTab('rewards') && (
                  <FaCrown
                    className="navButton-premiumCrown"
                    aria-label="Premium"
                  />
                )}
              </button>
            </li>
            <li>
              <button
                className={`navButton ${showPremiumCrownForTab('spotify') ? 'premium-feature' : ''} ${activeTab === 'spotify' ? 'active' : ''}`}
                onClick={() => switchTab('spotify')}
                title={
                  showPremiumCrownForTab('spotify')
                    ? 'Spotify (Premium)'
                    : undefined
                }
              >
                <SpotifyIcon />
                <span>Spotify</span>
                {showPremiumCrownForTab('spotify') && (
                  <FaCrown
                    className="navButton-premiumCrown"
                    aria-label="Premium"
                  />
                )}
              </button>
            </li>
            <li>
              <button
                className={`navButton ${showPremiumCrownForTab('followed') ? 'premium-feature' : ''} ${
                  activeTab === 'followed' ? 'active' : ''
                }`}
                onClick={() => switchTab('followed')}
                title={
                  showPremiumCrownForTab('followed')
                    ? 'Chaînes suivies (Premium)'
                    : undefined
                }
              >
                <FollowIcon />
                <span>Chaînes suivies</span>
                {hasNewFollowedChannelOnline && (
                  <span className="notification-badge"></span>
                )}
                {showPremiumCrownForTab('followed') && (
                  <FaCrown
                    className="navButton-premiumCrown"
                    aria-label="Premium"
                  />
                )}
              </button>
            </li>
            <li>
              <button
                className={`navButton ${showPremiumCrownForTab('mentions') ? 'premium-feature' : ''} ${
                  activeTab === 'mentions' ? 'active' : ''
                }`}
                onClick={() => switchTab('mentions')}
                title={
                  showPremiumCrownForTab('mentions')
                    ? 'Mentions (Premium)'
                    : undefined
                }
              >
                <MentionsIcon />
                <span>Mentions</span>
                {hasNewMentions && (
                  <span className="notification-badge"></span>
                )}
                {showPremiumCrownForTab('mentions') && (
                  <FaCrown
                    className="navButton-premiumCrown"
                    aria-label="Premium"
                  />
                )}
              </button>
            </li>
            <li>
              <button
                className={`navButton ${showPremiumCrownForTab('followssubs') ? 'premium-feature' : ''} ${
                  activeTab === 'followssubs' ? 'active' : ''
                }`}
                onClick={() => switchTab('followssubs')}
                title={
                  showPremiumCrownForTab('followssubs')
                    ? 'Follows & Subs (Premium)'
                    : undefined
                }
              >
                <FollowsSubsIcon />
                <span>Follows & Subs</span>
                {showPremiumCrownForTab('followssubs') && (
                  <FaCrown
                    className="navButton-premiumCrown"
                    aria-label="Premium"
                  />
                )}
              </button>
            </li>
          </NavSection>

          <NavSection
            sectionId="stream"
            title="Stream & contenu"
            isOpen={sectionOpen.stream}
            onToggle={() => toggleSection('stream')}
          >
            <li>
              <button
                className={`navButton ${showPremiumCrownForTab('streamstats') ? 'premium-feature' : ''} ${
                  activeTab === 'streamstats' ? 'active' : ''
                }`}
                onClick={() => switchTab('streamstats')}
                title={
                  showPremiumCrownForTab('streamstats')
                    ? 'Stats de stream (Premium)'
                    : undefined
                }
              >
                <StatsIcon />
                <span>Stats de Stream</span>
                {showPremiumCrownForTab('streamstats') && (
                  <FaCrown
                    className="navButton-premiumCrown"
                    aria-label="Premium"
                  />
                )}
              </button>
            </li>
            <li>
              <button
                className={`navButton ${showPremiumCrownForTab('birthdays') ? 'premium-feature' : ''} ${
                  activeTab === 'birthdays' ? 'active' : ''
                }`}
                onClick={() => switchTab('birthdays')}
                title={
                  showPremiumCrownForTab('birthdays')
                    ? 'Anniversaires (Premium)'
                    : undefined
                }
              >
                <BirthdayIcon />
                <span>Anniversaires</span>
                {showPremiumCrownForTab('birthdays') && (
                  <FaCrown
                    className="navButton-premiumCrown"
                    aria-label="Premium"
                  />
                )}
              </button>
            </li>
            <li>
              <button
                className={`navButton ${showPremiumCrownForTab('clips') ? 'premium-feature' : ''} ${activeTab === 'clips' ? 'active' : ''}`}
                onClick={() => switchTab('clips')}
                title={
                  showPremiumCrownForTab('clips')
                    ? 'Clips (Premium)'
                    : undefined
                }
              >
                <ClipsIcon />
                <span>Clips</span>
                {showPremiumCrownForTab('clips') && (
                  <FaCrown
                    className="navButton-premiumCrown"
                    aria-label="Premium"
                  />
                )}
              </button>
            </li>
            <li>
              <button
                className={`navButton ${showPremiumCrownForTab('overlays') ? 'premium-feature' : ''} ${activeTab === 'overlays' ? 'active' : ''}`}
                onClick={() => switchTab('overlays')}
                title={
                  showPremiumCrownForTab('overlays')
                    ? 'Overlays (Premium)'
                    : undefined
                }
              >
                <OverlaysIcon />
                <span>Overlays</span>
                {showPremiumCrownForTab('overlays') && (
                  <FaCrown
                    className="navButton-premiumCrown"
                    aria-label="Premium"
                  />
                )}
              </button>
            </li>
          </NavSection>

          <NavSection
            sectionId="automation"
            title="Automatisation"
            isOpen={sectionOpen.automation}
            onToggle={() => toggleSection('automation')}
          >
            <li>
              <button
                className={`navButton ${showPremiumCrownForTab('commands') ? 'premium-feature' : ''} ${activeTab === 'commands' ? 'active' : ''}`}
                onClick={() => switchTab('commands')}
                title={
                  showPremiumCrownForTab('commands')
                    ? 'Commandes (Premium)'
                    : undefined
                }
              >
                <CommandsIcon />
                <span>Commandes</span>
                {showPremiumCrownForTab('commands') && (
                  <FaCrown
                    className="navButton-premiumCrown"
                    aria-label="Premium"
                  />
                )}
              </button>
            </li>
            <li>
              <button
                className={`navButton ${showPremiumCrownForTab('ai') ? 'premium-feature' : ''} ${activeTab === 'ai' ? 'active' : ''}`}
                onClick={() => switchTab('ai')}
                title={
                  showPremiumCrownForTab('ai')
                    ? 'Configuration IA (Premium)'
                    : undefined
                }
              >
                <FaRobot aria-hidden />
                <span>Configuration IA</span>
                {showPremiumCrownForTab('ai') && (
                  <FaCrown
                    className="navButton-premiumCrown"
                    aria-label="Premium"
                  />
                )}
              </button>
            </li>
          </NavSection>

          <NavSection
            sectionId="settings"
            title="Réglages"
            isOpen={sectionOpen.settings}
            onToggle={() => toggleSection('settings')}
          >
            <li>
              <button
                className={`navButton ${showPremiumCrownForTab('parameters') ? 'premium-feature' : ''} ${
                  activeTab === 'parameters' ? 'active' : ''
                }`}
                onClick={() => switchTab('parameters')}
                title={
                  showPremiumCrownForTab('parameters')
                    ? 'Paramètres (Premium)'
                    : undefined
                }
              >
                <SettingsIcon />
                <span>Paramètres</span>
                {showPremiumCrownForTab('parameters') && (
                  <FaCrown
                    className="navButton-premiumCrown"
                    aria-label="Premium"
                  />
                )}
              </button>
            </li>
          </NavSection>
        </ul>
      </nav>
      <div className="sidebarVersion" title="Version en cours de développement">
        {APP_VERSION}
      </div>
    </aside>
  );
}

export default Sidebar;
